import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useYupValidationResolver } from 'hooks/useYupValidationResolver';
import useLayoutController from 'components/Stepper/RouteBasedStepper/useLayoutController';
import useQueryParams from 'hooks/helpers/useQueryParams';
import { useCreateCaseState } from './CreateCaseManager';

const useTypeOfOrder = () => {
    const { getParam } = useQueryParams();
    const {
        saveCaseManagementData,
        typeOfOrderData,
        currentPortfolioSelector,
        isJA,
        isPOA,
        isPA,
        isPK,
        isAzp,
        mainOwner,
        hasPendingCase,
    } = useCreateCaseState();

    const currentDepotValue = useMemo(() => currentPortfolioSelector?.CurrentValue, [currentPortfolioSelector?.CurrentValue]);
    const maxWithdrawalAmount = useMemo(() => (isPK ? currentDepotValue : ((currentDepotValue * 95) / 100).toFixed(2)), [currentDepotValue, isPK]); // 95% of the current depot value

    const ERROR_WITHDRAWAL_AMOUNT_MIN = 'createNewCase.TypeOfOrder.Error.minWithdrawalAmount';
    const ERROR_WITHDRAWAL_AMOUNT_MAX = 'createNewCase.TypeOfOrder.Error.maxWithdrawalAmount';

    // Validation schema for the form
    const schema = yup.object({
        OrderTypeId: yup.string(),
        Amount: yup.number()
            .test(
                'max-withdrawal-amount',
                ERROR_WITHDRAWAL_AMOUNT_MAX,
                function maxWithdrawalAmountTest(this: yup.TestContext, value) {
                    const { OrderTypeId } = this.parent;

                    if (OrderTypeId === '1') {
                        return value < maxWithdrawalAmount;
                    }

                    return true; // No limit for 'full' type of order
                },
            )
            .min(1, ERROR_WITHDRAWAL_AMOUNT_MIN)
            .typeError(ERROR_WITHDRAWAL_AMOUNT_MIN)
            .nullable(false, ERROR_WITHDRAWAL_AMOUNT_MIN)
            .required(ERROR_WITHDRAWAL_AMOUNT_MIN),
        NeedCloseAccount: yup.boolean(),
    });

    type FormData = yup.InferType<typeof schema>;

    const amountToBeSold = +(getParam('amount') as string);

    const getOrderTypeId = (): string => {
        if (+amountToBeSold === currentDepotValue) return '2';

        return '1';
    };

    const {
        register, control, handleSubmit, watch, reset, trigger, formState: { errors },
    } = useForm<FormData>({
        resolver: useYupValidationResolver(schema),
        defaultValues: { Amount: amountToBeSold || 0, OrderTypeId: getOrderTypeId(), ...typeOfOrderData },
    });

    const formData = watch();

    // Submit function to handle form submission
    const onSubmit = useCallback(async (data: FormData) => {
        if (data) {
            saveCaseManagementData({
                typeOfOrderData: data,
            });
            setStepComplete(true);
        }
    }, [saveCaseManagementData, currentDepotValue]);

    const { setStepComplete } = useLayoutController({
        callbackOnContinue: handleSubmit(onSubmit),
        isContinueDisabled: !formData?.OrderTypeId || Object.entries(errors)?.length > 0,
        isBackDisabled: true,
        reSubscriptionStates: formData?.OrderTypeId,
    });

    const handleTypeOfOrder = useCallback((value: string) => {
        reset({ OrderTypeId: value, Amount: value === '2' ? currentDepotValue : (amountToBeSold || 0) });
    }, [reset, currentDepotValue, amountToBeSold]);

    return {
        data: formData,
        register,
        control,
        setData: reset,
        trigger,
        errors,
        currentDepotValue,
        maxWithdrawalAmount,
        hasPendingSalesOrder: hasPendingCase,
        handleTypeOfOrder,
        isJA,
        isPOA,
        isPA,
        isPK,
        isAzp,
        mainOwner,
    };
};

export default useTypeOfOrder;
